import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, ShieldCheck, MessageCircleHeart, Phone, BookHeart, Wind, Settings, LogOut, HeartPulse, Share2, Download, Bell, Crown, Sparkles, Building2, FileSignature, Lock, Hospital, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CrisisBar from "./CrisisBar";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const NAV = [
    { to: "/", label: t("nav.home"), icon: Home, id: "nav-home" },
    { to: "/checkin", label: t("nav.checkin"), icon: HeartPulse, id: "nav-checkin" },
    { to: "/plan", label: t("nav.plan"), icon: ShieldCheck, id: "nav-plan" },
    { to: "/wrap", label: t("nav.wrap"), icon: Sparkles, id: "nav-wrap" },
    { to: "/assistant", label: t("nav.assistant"), icon: MessageCircleHeart, id: "nav-assistant" },
    { to: "/contacts", label: t("nav.contacts"), icon: Phone, id: "nav-contacts" },
    { to: "/mood", label: t("nav.mood"), icon: BookHeart, id: "nav-mood" },
    { to: "/toolkit", label: t("nav.toolkit"), icon: Wind, id: "nav-toolkit" },
    { to: "/share", label: t("nav.share"), icon: Share2, id: "nav-share" },
    { to: "/export", label: t("nav.export"), icon: Download, id: "nav-export" },
    { to: "/reminders", label: t("nav.reminders"), icon: Bell, id: "nav-reminders" },
    { to: "/billing", label: t("nav.billing"), icon: Crown, id: "nav-billing" },
    { to: "/trust", label: t("nav.trust", "Trust"), icon: Building2, id: "nav-trust" },
    { to: "/dpa", label: "DPA", icon: FileSignature, id: "nav-dpa" },
    { to: "/nhs", label: "NHS Connect", icon: Hospital, id: "nav-nhs" },
    ...(user?.is_admin ? [{ to: "/admin", label: "Admin", icon: Lock, id: "nav-admin" }] : []),
    { to: "/get-app", label: "Get the app", icon: Smartphone, id: "nav-get-app" },
    { to: "/settings", label: t("nav.settings"), icon: Settings, id: "nav-settings" },
  ];

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden md:flex md:flex-col fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 p-6 z-30">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-teal-700 text-white flex items-center justify-center font-display font-bold">S</div>
          <div>
            <div className="font-display font-bold text-slate-900 leading-tight">{t("app.name")}</div>
            <div className="text-xs text-slate-500">{t("app.tagline")}</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={id}
              end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-teal-50 text-teal-900" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 pt-4 mt-4">
          <div className="text-xs text-slate-500 mb-1">{t("nav.signed_in_as")}</div>
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="layout-user-name">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          {user?.trust && <div className="text-xs text-teal-700 truncate mt-0.5">{user.trust}</div>}
          {user?.is_admin && <div className="text-xs px-2 py-0.5 mt-1 inline-block rounded-full bg-rose-50 text-rose-800 font-semibold">Site admin</div>}
          <div className="mt-2 inline-flex text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-800" data-testid="layout-mode-badge">
            {user?.mode === "inpatient" ? t("nav.inpatient_mode") : t("nav.community_mode")}
          </div>
          <button
            data-testid="layout-logout"
            onClick={handleLogout}
            className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-xl py-2"
          >
            <LogOut className="w-4 h-4" /> {t("nav.sign_out")}
          </button>
        </div>
      </aside>

      <main className="md:ms-64 p-4 md:p-8 pb-24 md:pb-12 max-w-5xl mx-auto">
        {children}
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 px-2 py-1.5 flex justify-between">
        {NAV.slice(0, 5).map(({ to, label, icon: Icon, id }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`m-${id}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg flex-1 text-[10px] font-medium ${
                isActive ? "text-teal-700" : "text-slate-500"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <CrisisBar />
    </div>
  );
}
